import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useAuth } from '../../lib/AuthContext.jsx';
import { useNowMinute } from '../../lib/hooks.js';
import { Avatar, Stars, Loading, Empty, useToast, Field, KeyValue } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { currency, formatDayLabel, timeRange, todayISO, formatTime, formatDate, firstName } from '../../lib/format.js';
import ServiceIcon from '../../components/ServiceIcon.jsx';
import VisitLocation from '../../components/VisitLocation.jsx';

/** Splits a nurse's window into hourly start options. */
function slotStarts(window) {
  const out = [];
  const [sh, sm] = window.start_time.split(':').map(Number);
  const [eh] = window.end_time.split(':').map(Number);
  for (let h = sh; h < eh; h++) out.push(`${String(h).padStart(2, '0')}:${String(sm).padStart(2, '0')}`);
  return out;
}

const addHours = (hhmm, n) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${String(Math.min(h + n, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const STEPS = ['Service', 'Schedule', 'Details', 'Confirm'];

export default function Book() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [nurse, setNurse] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);

  const [serviceId, setServiceId] = useState(params.get('serviceId') || '');
  const [date, setDate] = useState(params.get('date') || '');
  const [start, setStart] = useState(params.get('start') || '');
  const [duration, setDuration] = useState(2);
  // One object so the location mode and the fields it drives stay in step.
  const [location, setLocation] = useState({ mode: 'other', address: '', city: '', lat: null, lng: null });
  const [notes, setNotes] = useState('');
  const now = useNowMinute(); // ticks every minute so expired slots drop live

  const patchLocation = (patch) => setLocation((l) => ({ ...l, ...patch }));
  const { address, city } = location;

  useEffect(() => {
    api.get(`/api/nurses/${id}`).then(setNurse).catch((e) => setError(e.message));
  }, [id]);

  // Default to the address already on file: the common case is a visit at home,
  // which then needs no input at all and lets the patient skip the step.
  useEffect(() => {
    if (!user) return;
    setLocation((l) =>
      l.address || !user.address
        ? l
        : { mode: 'saved', address: user.address, city: user.city || '', lat: null, lng: null }
    );
  }, [user]);

  // Deep links from the profile page can pre-fill the first two steps; start the
  // wizard on the first step the patient still has to fill in. A link naming a
  // service this nurse does not offer (stale bookmark, hand-edited URL) must not
  // skip step 1 — the booking would only fail at submit.
  useEffect(() => {
    if (!nurse) return;
    const linked = params.get('serviceId');
    const offered = nurse.services.some((s) => String(s.id) === String(linked));
    if (!offered) {
      if (linked) setServiceId('');
      return;
    }
    setStep(params.get('date') ? 2 : 1);
  }, [nurse, params]);

  const { days, windows, freeStarts, allExpired } = useMemo(() => {
    if (!nurse) return { days: [], windows: [], freeStarts: [], allExpired: false };

    const taken = (d, s, e) => nurse.bookedSlots.some((b) => b.date === d && b.start_time < e && s < b.end_time);

    const grouped = {};
    for (const slot of nurse.availability) {
      if (slot.date < todayISO()) continue;
      (grouped[slot.date] ||= []).push(slot);
    }
    const days = Object.keys(grouped).sort().slice(0, 21);
    const windows = date ? grouped[date] || [] : [];
    const isToday = date === todayISO();

    let generated = 0;
    const freeStarts = [];
    for (const w of windows) {
      for (const s of slotStarts(w)) {
        const e = addHours(s, duration);
        generated += 1;
        // On today, a start time that has already passed is hidden entirely
        // (not just disabled) — the patient can only pick a future slot.
        if (isToday && s <= now) continue;
        freeStarts.push({ start: s, end: e, disabled: e > w.end_time || taken(date, s, e) });
      }
    }
    // Distinguish "everything already passed today" from "the day is empty".
    const allExpired = isToday && generated > 0 && freeStarts.length === 0;
    return { days, windows, freeStarts, allExpired };
  }, [nurse, date, duration, now]);

  // Drop a previously chosen start once it slips into the past (deep link, or
  // the clock ticking past it while the wizard is open).
  useEffect(() => {
    if (start && date === todayISO() && start <= now) setStart('');
  }, [now, date, start]);

  const service = nurse?.services.find((s) => String(s.id) === String(serviceId));
  const end = start ? addHours(start, duration) : '';
  const amount = nurse ? duration * nurse.hourly_rate : 0;

  // A visit somewhere other than a known address is only pinned down once the
  // map has a point — the text alone has been wrong often enough to matter.
  const locationReady = !!address.trim() && (location.mode !== 'other' || location.lat != null);
  const stepValid = [!!service, !!(date && start), locationReady, true];
  const canAdvance = stepValid[step];
  const summary = [
    service ? service.name : 'Not chosen',
    date && start ? `${formatDayLabel(date)}, ${formatTime(start)}` : 'Not chosen',
    address.trim() ? address.split('\n')[0].slice(0, 28) : 'Not entered',
    date && start ? currency(amount) : '—',
  ];

  const goTo = (next) => {
    // Only allow jumping back, or forward when every earlier step is valid.
    if (next <= step || stepValid.slice(0, next).every(Boolean)) {
      setStep(next);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const booking = await api.post('/api/bookings', {
        nurseId: Number(id),
        serviceId: Number(serviceId),
        date,
        startTime: start,
        endTime: end,
        address: address.trim(),
        city,
        notes,
        lat: location.lat,
        lng: location.lng,
      });
      toast.success(`Request sent to ${nurse.name} — you will be notified when they respond.`);
      navigate(`/bookings/${booking.id}`, { replace: true });
    } catch (err) {
      toast.error(err);
      // A slot conflict means the calendar moved under us — refresh and rewind.
      if (err.status === 409) {
        api.get(`/api/nurses/${id}`).then(setNurse).catch(() => {});
        setStart('');
        setStep(1);
      }
      setBusy(false);
    }
  };

  if (error)
    return (
      <Empty
        art="not-found"
        title="Cannot book this nurse"
        action={
          <Link to="/search" className="btn btn-primary">
            Find another nurse
          </Link>
        }
      >
        {error}
      </Empty>
    );
  if (!nurse) return <Loading rows={2} />;

  // A deep link — a bookmark, or a service tile on the profile — can reach a
  // nurse whose calendar is closed. Say so before the patient invests in
  // choosing a service and hits a wall on the next step.
  if (nurse.is_bookable === false) {
    return (
      <>
        <Link to={`/nurses/${id}`} className="back-link">
          <Icon name="arrow-left" />
          Back to profile
        </Link>
        <Empty
          art="schedule"
          title={nurse.is_available ? 'No open time slots' : 'Not accepting bookings'}
          actions={
            <>
              <Link to="/search" className="btn btn-primary">
                <Icon name="search" /> Find another nurse
              </Link>
              <Link to={`/nurses/${id}`} className="btn">
                View profile
              </Link>
            </>
          }
        >
          {nurse.is_available
            ? `${firstName(nurse.name)} hasn’t published any available time slots yet, so there is nothing to book. Check back later — nurses open their own calendar.`
            : `${firstName(nurse.name)} has paused new bookings for now.`}
        </Empty>
      </>
    );
  }

  return (
    <>
      <Link to={`/nurses/${id}`} className="back-link">
        <Icon name="arrow-left" />
        Back to profile
      </Link>

      <div className="page-head">
        <Avatar name={nurse.name} avatar={nurse.avatar} />
        <div className="grow">
          <h1>Book {nurse.name}</h1>
          <p className="row-tight" style={{ flexWrap: 'wrap' }}>
            <span>{currency(nurse.hourly_rate)}/hr</span>
            <span className="dim">·</span>
            <Stars value={nurse.rating_avg} count={nurse.rating_count} />
          </p>
        </div>
      </div>

      <nav className="wizard-rail" aria-label="Booking steps">
        {STEPS.map((name, i) => (
          <button
            key={name}
            type="button"
            data-state={i === step ? 'current' : i < step && stepValid[i] ? 'done' : 'todo'}
            disabled={i > step && !stepValid.slice(0, i).every(Boolean)}
            onClick={() => goTo(i)}
            aria-current={i === step ? 'step' : undefined}
          >
            <span className="wizard-bar" />
            <span className="wizard-name">
              {i + 1}. {name}
            </span>
            <span className="wizard-value">{summary[i]}</span>
          </button>
        ))}
      </nav>

      {/* With the sidebar gone on the confirm step the column would stretch the
          full page width, so it drops to a single readable measure instead. */}
      <div className={step < 3 ? 'detail-layout' : 'wizard-confirm'}>
        <div>
          <div className="step-panel" key={step}>
            {/* ------------------------------------------- 1. Service */}
            {step === 0 && (
              <section className="card">
                <div className="card-header">
                  <h2 className="grow">What care do you need?</h2>
                </div>
                <div className="card-body">
                  <div className="grid grid-3">
                    {nurse.services.map((s, i) => (
                      <button
                        key={s.id}
                        type="button"
                        className="service-tile anim-rise"
                        style={{ '--i': i }}
                        data-selected={String(s.id) === String(serviceId) ? 'true' : 'false'}
                        onClick={() => {
                          setServiceId(String(s.id));
                          goTo(1);
                        }}
                      >
                        <span className="ico" aria-hidden="true">
                          <ServiceIcon service={s} size={26} />
                        </span>
                        <span className="nm">{s.name}</span>
                      </button>
                    ))}
                  </div>
                  <p className="tiny dim mt-2">Only the services {nurse.name} is qualified for are shown here.</p>
                </div>
              </section>
            )}

            {/* ------------------------------------------ 2. Schedule */}
            {step === 1 && (
              <section className="card">
                <div className="card-header">
                  <h2>When should the nurse come?</h2>
                </div>
                <div className="card-body">
                  {days.length === 0 ? (
                    <Empty
                      art="schedule"
                      title="No open slots"
                      action={
                        <Link to="/search" className="btn btn-primary">
                          Find another nurse
                        </Link>
                      }
                    >
                      This nurse has not published availability yet. Try another nurse or check back later.
                    </Empty>
                  ) : (
                    <>
                      <span className="label mb-1">Pick a day</span>
                      <div className="day-strip">
                        {days.map((d) => (
                          <button
                            key={d}
                            type="button"
                            className="day-pill"
                            aria-pressed={date === d}
                            onClick={() => {
                              setDate(d);
                              setStart('');
                            }}
                          >
                            <span>{formatDayLabel(d).slice(0, 3)}</span>
                            <b>{Number(d.slice(8, 10))}</b>
                            <span>{new Date(`${d}T12:00:00`).toLocaleDateString('en-IN', { month: 'short' })}</span>
                          </button>
                        ))}
                      </div>

                      <Field label="How long do you need?">
                        <div className="chips">
                          {[1, 2, 3, 4, 6, 8, 12].map((h) => (
                            <button
                              key={h}
                              type="button"
                              className="chip"
                              aria-pressed={duration === h}
                              onClick={() => {
                                setDuration(h);
                                setStart('');
                              }}
                            >
                              {h} hr{h > 1 ? 's' : ''} · {currency(h * nurse.hourly_rate)}
                            </button>
                          ))}
                        </div>
                      </Field>

                      {date ? (
                        <>
                          <span className="label">Start time</span>
                          {windows.length > 0 && (
                            <p className="tiny dim" style={{ margin: '4px 0 10px' }}>
                              {firstName(nurse.name)} works{' '}
                              {windows.map((w) => timeRange(w.start_time, w.end_time)).join(' and ')} on{' '}
                              {formatDayLabel(date).toLowerCase()}.
                            </p>
                          )}
                          {allExpired ? (
                            <div className="banner banner-warning">
                              <Icon name="clock" />
                              <span className="grow">
                                No available time slots remaining for today. Please choose another date.
                              </span>
                            </div>
                          ) : freeStarts.every((s) => s.disabled) ? (
                            <div className="banner banner-warning">
                              <Icon name="warning" />
                              <span className="grow">
                                No {duration}-hour slot fits on this day. Try a shorter duration or another date.
                              </span>
                            </div>
                          ) : (
                            <div className="slot-grid">
                              {freeStarts.map((s) => (
                                <button
                                  key={s.start}
                                  type="button"
                                  className="slot"
                                  aria-pressed={start === s.start}
                                  disabled={s.disabled}
                                  title={s.disabled ? 'Unavailable' : `${formatTime(s.start)} – ${formatTime(s.end)}`}
                                  onClick={() => setStart(s.start)}
                                >
                                  {formatTime(s.start)}
                                </button>
                              ))}
                            </div>
                          )}
                          {start && (
                            <div className="banner banner-success mt-2" style={{ marginBottom: 0 }}>
                              <Icon name="check-circle" />
                              <span className="grow">
                                {formatDayLabel(date)}, {timeRange(start, end)} — {currency(amount)}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="small dim">Select a day above to see available start times.</p>
                      )}
                    </>
                  )}
                </div>
              </section>
            )}

            {/* ------------------------------------------- 3. Details */}
            {step === 2 && (
              <section className="card">
                <div className="card-header">
                  <h2>Where should the nurse come?</h2>
                </div>
                <div className="card-body">
                  <VisitLocation
                    value={location}
                    onChange={patchLocation}
                    saved={{ address: user?.address, city: user?.city }}
                    onResolved={() => goTo(3)}
                  />

                  <div className="banner banner-info">
                    <Icon name="cash" />
                    <span className="grow">
                      Pay {nurse.name} in cash at the end of the visit — {currency(nurse.hourly_rate)} per hour,{' '}
                      {currency(amount)} for {duration} hour{duration > 1 ? 's' : ''}.
                    </span>
                  </div>

                  <Field
                    label="Notes for the nurse"
                    id="notes"
                    hint="Conditions, mobility needs, allergies, access instructions."
                    optional
                  >
                    <textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Patient is 78, recovering from a hip replacement. Lift available."
                    />
                  </Field>
                </div>
              </section>
            )}

            {/* ------------------------------------------- 4. Confirm */}
            {/* The running sidebar summary is hidden on this step and folded in
                here, so the final screen states the booking once rather than
                twice in two slightly different shapes. */}
            {step === 3 && (
              <section className="card">
                <div className="card-header">
                  <h2 className="grow">Review your request</h2>
                  <Icon name="clipboard" size={16} className="dim" />
                </div>

                {/* Who it is with — carried over from the old sidebar. */}
                <div className="card-body" style={{ paddingBottom: 0 }}>
                  <div className="row-tight">
                    <Avatar name={nurse.name} avatar={nurse.avatar} />
                    <div className="grow">
                      <div className="t-title truncate">{nurse.name}</div>
                      <div className="tiny dim">
                        {currency(nurse.hourly_rate)}/hr · {nurse.city}
                      </div>
                    </div>
                    <Stars value={nurse.rating_avg} count={nurse.rating_count} />
                  </div>
                  <hr className="divider" />
                </div>

                <div className="card-body" style={{ paddingTop: 0 }}>
                  <dl style={{ margin: 0 }}>
                    <KeyValue label="Service" action={<EditLink onClick={() => goTo(0)} />}>
                      {service?.name}
                    </KeyValue>
                    <KeyValue label="Date" action={<EditLink onClick={() => goTo(1)} />}>
                      {formatDayLabel(date)}, {formatDate(date)}
                    </KeyValue>
                    <KeyValue label="Time" action={<EditLink onClick={() => goTo(1)} />}>
                      {timeRange(start, end)} ({duration} hr{duration > 1 ? 's' : ''})
                    </KeyValue>
                    <KeyValue label="Address" action={<EditLink onClick={() => goTo(2)} />}>
                      {address}
                      {city ? `, ${city}` : ''}
                    </KeyValue>
                    {notes && (
                      <KeyValue label="Notes" action={<EditLink onClick={() => goTo(2)} />}>
                        {notes}
                      </KeyValue>
                    )}
                  </dl>

                  <hr className="divider" />

                  {/* The price breakdown the sidebar used to carry. */}
                  <dl style={{ margin: 0 }}>
                    <KeyValue label={`${currency(nurse.hourly_rate)} × ${duration} hour${duration > 1 ? 's' : ''}`}>
                      {currency(amount)}
                    </KeyValue>
                    <KeyValue label="Payment">Cash, after the visit</KeyValue>
                  </dl>

                  <div className="summary-total">
                    <span>Total payable</span>
                    <span className="rate">{currency(amount)}</span>
                  </div>

                  <div className="banner banner-info mt-3" style={{ marginBottom: 0 }}>
                    <Icon name="info" />
                    <span className="grow tiny">
                      Nothing is charged now. {nurse.name} has to accept the request first — you will be notified
                      either way and can cancel free of charge.
                    </span>
                  </div>
                </div>
              </section>
            )}

            {/* ------------------------------------ Sticky step actions */}
            <div className="wizard-actions">
              <button className="btn" onClick={() => goTo(step - 1)} disabled={step === 0 || busy}>
                <Icon name="arrow-left" />
                <span className="sr-only">Back</span>
              </button>
              <div className="grow">
                {!canAdvance && step < 3 && (
                  <span className="tiny dim">
                    {
                      [
                        'Choose a service to continue.',
                        'Pick a day and a start time.',
                        location.mode === 'other' && location.lat == null
                          ? 'Confirm the spot on the map so the nurse can find the place.'
                          : 'Enter the visit address.',
                      ][step]
                    }
                  </span>
                )}
              </div>
              {step < 3 ? (
                <button className="btn btn-primary btn-lg" disabled={!canAdvance} onClick={() => goTo(step + 1)}>
                  Continue <Icon name="arrow-right" />
                </button>
              ) : (
                <button className="btn btn-primary btn-lg" disabled={busy} onClick={submit}>
                  {busy ? <span className="spinner" /> : `Confirm · ${currency(amount)}`}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Running summary — only useful while the booking is still being
            assembled. On the confirm step it is folded into the review card. */}
        {step < 3 && (
          <aside>
            <section className="card sticky-aside">
              <div className="card-header">
                <h3 className="grow">Summary</h3>
                <Icon name="clipboard" size={16} className="dim" />
              </div>
              <div className="card-body">
                <div className="row-tight mb-2">
                  <Avatar name={nurse.name} avatar={nurse.avatar} size="sm" />
                  <div className="grow">
                    <div className="bold small truncate">{nurse.name}</div>
                    <div className="tiny dim">{currency(nurse.hourly_rate)}/hr</div>
                  </div>
                </div>
                <dl style={{ margin: 0 }}>
                  <KeyValue label="Service">{service ? service.name : '—'}</KeyValue>
                  <KeyValue label="Date">{date ? formatDayLabel(date) : '—'}</KeyValue>
                  <KeyValue label="Time">{start ? timeRange(start, end) : '—'}</KeyValue>
                  <KeyValue label={`${currency(nurse.hourly_rate)} × ${duration} hr${duration > 1 ? 's' : ''}`}>
                    {currency(amount)}
                  </KeyValue>
                </dl>
                <div className="summary-total">
                  <span>Total</span>
                  <span className="rate">{currency(amount)}</span>
                </div>
                <p className="tiny dim mt-2">Paid in cash to the nurse after the visit.</p>
              </div>
            </section>
          </aside>
        )}
      </div>
    </>
  );
}

/** Inline "edit" affordance that jumps the wizard back to a step. */
function EditLink({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: 'none',
        background: 'none',
        color: 'var(--accent)',
        cursor: 'pointer',
        padding: '0 0 0 8px',
        fontWeight: 600,
        fontSize: 'var(--fs-small)',
      }}
    >
      Edit
    </button>
  );
}
